// ГЕЙТ ДЕКИ (ШАГ 3) — арифметика пола, перенесённой метки и двух блоков финала. B5-1
// `3e519875cbb881648931f12b9fd2c991`.
//
// ИСТОЧНИК — `<script>` борда `Life Map - s3 Deck v8.html`: `nSliders` (`:423`), `FLOOR` (`:430`),
// `gateOK` (`:431`), `underFloor` (`:432`), `carried` (`:441`) и разбиение на два блока в
// `done()` (`:644–649`). Каждое утверждение ниже снято с кадра `Edge states v2` — таблица
// истинности живёт в `deckGate.test.js`, входы задаются теми же пресетами, что у борда.
//
// ПОЧЕМУ ОТДЕЛЬНЫЙ МОДУЛЬ — та же причина, что у `stageGate.js` шага 2: здесь утверждения о
// СОСТОЯНИИ, и проверять их надо без монтирования и без браузера. Плюс своя причина: гейт деки
// читают двое — карточка (замок панелей B/C) и финал (две стены), — и функция, которую зовут
// двое, не имеет права быть замыканием одного из них.
//
// ⛔ ГЕЙТ — ФУНКЦИЯ СОСТОЯНИЯ, А НЕ СОБЫТИЯ. Так сказано в байте борда (`:429`, «Function of
// state, never of a tap») и так проверяют два кадра: `B2` (возврат назад в заполненную карточку)
// и `C4` (resume прямо внутрь недобранной) обязаны дать ту же картинку, что прямой вход. Отсюда
// форма всех функций здесь: на входе значения, на выходе ответ, никакой истории переходов.
//
// ⚠️ ЕДИНСТВЕННОЕ ИСКЛЮЧЕНИЕ ИЗ ПРАВИЛА ВЫШЕ — ПАМЯТЬ СТЕНЫ, и она обязана быть. «Стена когда-то
// говорила» из текущих значений не выводится: недоборы к этому моменту уже починены. Живёт она
// записью `'s3/wall'` в `revealsPlayed` конверта (`s903.d2`) — то есть скоуп сессии, сброс только
// на `start over`, перезагрузку вкладки переживает. Отдельного поля нет, форма конверта не
// меняется.
//
// ⛔ `cardDone` БОРДА (`:427`) СЮДА НЕ ПЕРЕНЕСЁН НАМЕРЕННО. Это мёртвое определение: во всём
// файле борда единственное вхождение — само объявление, функция не зовётся нигде, и CD написал
// об этом прямо в `VERSIONS-cd.md` («чистка — на переносе в код»). Перенести её как «правило
// полноты карточки» было бы ошибкой: полнота карточки в гейт не входит вовсе.

/**
 * Пол панели A — два сведённых ползунка на карточку (борд `:430`).
 *
 * Не «половина семи» и не «сколько-нибудь»: два — это минимум, на котором линия получает
 * направление. Ниже неё рисовать нечего, выше — уже вопрос богатства, а не корректности.
 */
export const FLOOR = 2;

/** Токен памяти стены в `revealsPlayed` (`s903.d2`). Форма — адресоподобная, как у хрома §4. */
export const WALL_TOKEN = 's3/wall';

/** Сколько ползунков сведено на карточке. `null` = не тронут (`s443.d5` — легальное значение). */
export function nSliders(row) {
  return Array.isArray(row) ? row.filter((v) => v !== null && v !== undefined).length : 0;
}

/** Открыт ли гейт карточки: сведено не меньше двух ползунков. */
export function gateOK(row) {
  return nSliders(row) >= FLOOR;
}

/** Индексы карточек под полом — в порядке колоды (борд `:432`). */
export function underFloor(vals) {
  if (!Array.isArray(vals)) return [];
  return vals.map((_, i) => i).filter((i) => !gateOK(vals[i]));
}

/**
 * Перенесённая метка (`moved last card`, борд `:441`): слой, сведённый на ПРЕДЫДУЩЕЙ карточке и
 * не тронутый на этой.
 *
 * ⛔ МЕТКА КАСАНИЕМ НЕ СЧИТАЕТСЯ, И ЭТО НЕ СЛЕДСТВИЕ, А УСЛОВИЕ (`s824.d7`, `s670.d2`). В байте
 * борда оно выражено формой самого предиката: `carried` требует `vals[i][li] === null`, то есть
 * помеченный ползунок по построению остаётся несведённым и в счёт пола не попадает. Уберите это
 * условие — и вторая карточка проходила бы гейт сама собой, ни разу не тронутая. Переносится
 * ВНИМАНИЕ, а не ответ.
 *
 * @param {Array<Array<number|null>>} vals ряды ползунков по этапам, старый первый
 * @param {number} i индекс карточки
 * @param {number} li индекс слоя
 */
export function carried(vals, i, li) {
  if (!Array.isArray(vals) || i <= 0) return false;
  const row = vals[i];
  const prev = vals[i - 1];
  if (!Array.isArray(row) || !Array.isArray(prev)) return false;
  return row[li] === null && prev[li] !== null && prev[li] !== undefined;
}

/** Маска перенесённых меток на карточке — семь булевых по порядку слоёв. */
export function carriedRow(vals, i) {
  const row = Array.isArray(vals) && Array.isArray(vals[i]) ? vals[i] : [];
  return row.map((_, li) => carried(vals, i, li));
}

/** Помнит ли конверт, что стена уже говорила в этой сессии (`s903.d2`). */
export function wallSpoken(revealsPlayed) {
  return Array.isArray(revealsPlayed) && revealsPlayed.includes(WALL_TOKEN);
}

/**
 * Записать память стены. Возвращает НОВЫЙ массив (конверт не мутируется) и тот же самый, если
 * токен уже стоит: повторная запись сдвинула бы ссылку и заставила автосейв писать конверт,
 * в котором ничего не изменилось.
 */
export function rememberWall(revealsPlayed) {
  const played = Array.isArray(revealsPlayed) ? revealsPlayed : [];
  return wallSpoken(played) ? played : [...played, WALL_TOKEN];
}

/**
 * Два блока финала — жёсткий (стена) и тихий, ВЗАИМОИСКЛЮЧАЮЩИЕ (борд `:644–649`).
 *
 * `hard` — карточки под полом: линии нечем идти через этап. `soft` — карточки РОВНО НА ПОЛУ:
 * вход сузился до дна (`nSliders === FLOOR`), при трёх и выше экран молчит. Группы в счёт не
 * входят вовсе — тихий блок и гейт считают одно и то же.
 *
 * ⭐ ВАРИАНТ B, ПОДПИСАН 17.08. Тихий блок молчит в сессии, где стена уже говорила. В байте это
 * `QUIET_AFTER_WALL=false`, то есть условие сводится к `!WALL_FIRED`; отклонённый вариант A
 * (`mix2fixA`) оставлен в борде следом решения и каноном не является — поэтому переключателя
 * здесь нет ни одного. Почему так: оба блока — одна визуальная порода (заголовок, строка, ряд
 * римских кнопок), и сразу после стены тихий читается как «стена номер два», то есть как
 * сдвинутые ворота.
 *
 * ⚠️ ПОДАВЛЕНИЙ ДВА, И ОНИ РАЗНЫЕ. Первое — память: стена говорила когда-то, тихий блок снят до
 * конца сессии. Второе — одновременность: пока `hard` непуст, тихий не печатается вовсе (борд:
 * `!hard.length && soft.length`). Свести их в одно нельзя — они гасят разные случаи, и кадр
 * `E2a` (стена и дно на одной карте) держится именно вторым.
 *
 * `wall` — говорит ли стена ПРЯМО СЕЙЧАС; это сигнал вызывающему записать `WALL_TOKEN` в конверт.
 * Записывает не этот модуль: конверт принадлежит провайдеру, а чистая функция его не трогает.
 *
 * @param {Array<Array<number|null>>} vals ряды ползунков по этапам
 * @param {string[]} [revealsPlayed] `revealsPlayed` конверта
 * @returns {{hard: number[], soft: number[], wall: boolean}}
 */
export function deckBlocks(vals, revealsPlayed = []) {
  const rows = Array.isArray(vals) ? vals : [];
  const hard = underFloor(rows);
  const soft = (hard.length > 0 || wallSpoken(revealsPlayed))
    ? []
    : rows.map((_, i) => i).filter((i) => nSliders(rows[i]) === FLOOR);
  return { hard, soft, wall: hard.length > 0 };
}

/**
 * ДОСТИЖИМ ЛИ ФИНАЛ ИЗ ДЕКИ (`s922`, приёмка B5-2 → скоуп B5-6, пункт 4).
 *
 * ⭐ ВОПРОС ПРО АДРЕС, А НЕ ПРО КАРТИНКУ, и ответ на него — гейт ПОСЛЕДНЕЙ карточки. Через деку в
 * финал ведёт ровно одна дверь: CTA `draw the map` на панели C последней карточки, а панель C
 * заперта, пока `gateOK` этой карточки ложь (`paneLocked`). То есть «человек дошёл до финала»
 * и «последняя карточка прошла пол» — это одно и то же утверждение, просто сказанное с двух
 * сторон. Здесь оно сказано один раз, чтобы поправка адреса не завела второй правды о двери.
 *
 * ⛔ ЭТО НЕ «ВСЯ ДЕКА СОБРАНА». Карточки под полом в середине колоды — законное состояние
 * финала: ради них там и стоит стена. Потребуй здесь `underFloor().length === 0` — и стена стала
 * бы недостижимой, то есть пункт 1 скоупа отменил бы пункт 4.
 *
 * ЗАЧЕМ ВООБЩЕ. Адрес `#s3/{N+1}` разбирается грамматикой всегда, и число этапов может УМЕНЬШИТЬСЯ
 * на шаге 2. Тогда старый адрес карточки (`#s3/5` при пяти этапах) сам собой становится адресом
 * финала при трёх — и открывает его мимо деки, не спросив ничего.
 *
 * @param {Array<Array<number|null>>} vals ряды ползунков по этапам (короче числа этапов — законно)
 * @param {number} stageCount число этапов в конверте
 */
export function doneOpen(vals, stageCount) {
  if (!Number.isInteger(stageCount) || stageCount < 1) return false;
  const rows = Array.isArray(vals) ? vals : [];
  return gateOK(rows[stageCount - 1]);
}

/**
 * Заперты ли панели B и C (борд `:524`: `if(p>0 && !gateOK(S.i))`).
 *
 * Отдельной функцией, а не `!gateOK` на месте: замок — это утверждение про ПАНЕЛЬ, и панель A
 * не заперта никогда. Вызов с `pane === 0` обязан отвечать «нет», а не «гейт закрыт».
 */
export function paneLocked(row, pane) {
  return pane > 0 && !gateOK(row);
}
